import { type JSX, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import TuneIcon from '@mui/icons-material/Tune';
import DevicesIcon from '@mui/icons-material/Devices';
import ShieldIcon from '@mui/icons-material/Shield';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import AndroidIcon from '@mui/icons-material/Android';
import DownloadIcon from '@mui/icons-material/Download';

import { listDevices, type MeTenant } from '../api.js';
import { fleetHref } from './route.js';

const TIER_LABEL: Record<MeTenant['tier'], string> = { core: 'Core', pro: 'Pro', enterprise: 'Enterprise' };
const ONLINE_WINDOW_SEC = 15 * 60;

/**
 * Direct download for the signed Android kiosk APK — the no-MDM
 * distribution path. One generic build serves every fleet; the device
 * is bound to a fleet by the launcher URL entered (or QR-scanned) on
 * first launch. Override via `VITE_ANDROID_APK_URL` (Pages env) when the
 * APK moves behind a custom domain; falls back to the public R2 URL.
 */
const ANDROID_APK_URL: string =
  (import.meta.env['VITE_ANDROID_APK_URL'] as string | undefined) ||
  'https://pub-02fbdf0f9b1441fb8c19d013db8aaf7d.r2.dev/android/mobilauncher.apk';

/**
 * The per-fleet landing page: a few summary cards (plan, launcher link, device
 * count, security) plus quick links into the sections. First thing a signed-in
 * customer sees for the selected fleet.
 */
export function OverviewSection({
  tenant,
  totpEnabled,
  onManage2fa,
}: {
  tenant: MeTenant;
  totpEnabled: boolean;
  onManage2fa: () => void;
}): JSX.Element {
  const [devices, setDevices] = useState<{ total: number; online: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDevices(null);
    if (tenant.editToken === null) return;
    void listDevices({ tenantId: tenant.tenantId, editToken: tenant.editToken }).then((res) => {
      if (cancelled || res.kind !== 'ok') return;
      const now = Math.floor(Date.now() / 1000);
      const online = res.devices.filter((d) => d.lastSeenAt >= now - ONLINE_WINDOW_SEC).length;
      setDevices({ total: res.devices.length, online });
    });
    return () => {
      cancelled = true;
    };
  }, [tenant.tenantId, tenant.editToken]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h5" fontWeight={700}>
          {tenant.deploymentName}
        </Typography>
        <Chip
          size="small"
          color={tenant.tier === 'core' ? 'default' : 'secondary'}
          label={TIER_LABEL[tenant.tier]}
        />
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Overview of this fleet. Jump into a section from the sidebar, or use the shortcuts below.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
        }}
      >
        <SummaryCard icon={<LaunchIcon />} title="Launcher">
          <Link
            href={tenant.launcherUrl}
            target="_blank"
            rel="noopener"
            sx={{ wordBreak: 'break-all', fontSize: 14 }}
          >
            {tenant.launcherUrl}
          </Link>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button size="small" variant="contained" startIcon={<LaunchIcon />} href={tenant.launcherUrl} target="_blank" rel="noopener">
              Open
            </Button>
            <Button size="small" variant="outlined" startIcon={<TuneIcon />} href={fleetHref(tenant.tenantId, 'launcher')}>
              Customize
            </Button>
          </Stack>
        </SummaryCard>

        <SummaryCard icon={<DevicesIcon />} title="Devices">
          <Typography variant="h4" fontWeight={700}>
            {devices ? String(devices.total) : '—'}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {devices ? `${String(devices.online)} online in the last 15 min` : 'Loading…'}
          </Typography>
          <Button size="small" sx={{ mt: 1.5 }} href={fleetHref(tenant.tenantId, 'devices')}>
            Manage devices
          </Button>
        </SummaryCard>

        <SummaryCard icon={<ShieldIcon />} title="Security">
          <Chip
            size="small"
            color={totpEnabled ? 'success' : 'default'}
            label={totpEnabled ? 'Two-factor on' : 'Two-factor off'}
          />
          <Box>
            <Button size="small" sx={{ mt: 1.5 }} onClick={onManage2fa}>
              {totpEnabled ? 'Manage 2FA' : 'Set up 2FA'}
            </Button>
          </Box>
        </SummaryCard>

        <SummaryCard icon={<UpgradeIcon />} title="Plan">
          <Typography variant="h6" fontWeight={700}>
            {TIER_LABEL[tenant.tier]}
          </Typography>
          <Button size="small" sx={{ mt: 1.5 }} href={fleetHref(tenant.tenantId, 'subscription')}>
            {tenant.tier === 'core' ? 'Upgrade' : 'Manage subscription'}
          </Button>
        </SummaryCard>
      </Box>

      <Card variant="outlined" sx={{ mt: 2 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', mb: 1 }}>
                <AndroidIcon />
                <Typography variant="overline">Android app — no MDM required</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Sideload the kiosk app on any Android 8.0+ device. On first launch, enter this
                fleet&rsquo;s launcher URL (shown above) or scan a QR code — the device then locks
                into kiosk mode. Have an MDM like SOTI MobiControl instead? Point its lockdown
                profile straight at the launcher URL; the app is only needed without one.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href={ANDROID_APK_URL}
              sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              Download APK
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function SummaryCard({ icon, title, children }: { icon: JSX.Element; title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {icon}
          <Typography variant="overline">{title}</Typography>
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}
